import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import DFVPlayer from './DFVPlayer';
import { DFV_BG_COLOR_CHANNELS, DynamicFieldViewCanvas } from '../DynamicFieldView.style';
import CanvasVideoEventBus from '../utils/CanvasVideoEventBus';
import FPSLocker from '../utils/FPSLocker';

const VIDEO_RES_DEFAULTS = { width: 1280, height: 720 };
// const COLOR_CHANNEL_ACCURACY = 15;
// const COLOR_CHANNEL_ACCURACY_INVERSE = COLOR_CHANNEL_ACCURACY * -1;
const GAMMA_ACCURACY = 1;
const GAMMA_ACCURACY_INVERSE = GAMMA_ACCURACY * -1;
const SMOOTH = 'high';

// Placeholder for now, will address in follow-up spike.
const needsGammaAdjust = false;

const CANVAS_CONTEXT_CONFIG = {
    willReadFrequently: false, /* When doing color replacement, true */
    desynchronized: false,
    colorSpace: 'srgb'
};

let gammaCorrect = null;

const gammaDetect = ([r, g, b]) => {
    const rd = (r - DFV_BG_COLOR_CHANNELS.r);
    const gd = (g - DFV_BG_COLOR_CHANNELS.g);
    const bd = (b - DFV_BG_COLOR_CHANNELS.b);

    gammaCorrect = !(
        (rd >= 0 && rd <= GAMMA_ACCURACY && gd <= GAMMA_ACCURACY && bd <= GAMMA_ACCURACY) ||
        (rd < 0 && rd >= GAMMA_ACCURACY_INVERSE && gd >= GAMMA_ACCURACY_INVERSE && bd >= GAMMA_ACCURACY_INVERSE)
    );
};

const renderFrame = (context, video, videoResRef) => {
    if (context) {
        const { canvas } = context;
        if (canvas && canvas.parentNode && video && video.parentNode) {
            const { current: videoRes } = videoResRef;

            if (video.videoWidth && (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight)) {
                canvas.width = videoRes.width = video.videoWidth;
                canvas.height = videoRes.height = video.videoHeight;
            }

            // Go a little bigger for the clear - in case of subpixel rounding, etc.
            context.clearRect(0, 0, canvas.width + 1, canvas.height + 1);

            context.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Sample the upper left px and determine if we need
            // gamma correction, only once.
            if (gammaCorrect === null) {
                try {
                    let frame = context.getImageData(0, 0, 1, 1);

                    let { data: frameData } = frame;

                    if (frameData[3] !== 0) {
                        gammaDetect(frameData);
                    }
                    frameData = frame = null;
                } catch (imErr) {
                    // something went wrong - abort attempting gamma correction
                    gammaCorrect = false;
                }
            }
            if (needsGammaAdjust) {
                context.canvas.setAttribute('data-gamma-correct', gammaCorrect);
            }
            // Leaving this dormant, until we work out the color removal
            // This works, sort of - but the color range targeting
            // needs tweaking, as the color range starts approaching the wall color.

            // const frame = context.getImageData(x, y, width, height);
            // const { data: frameData } = frame;
            // const { length: len } = frameData;

            // if (frameData[3] !== 0) {

            //     const DFV_BG_COLOR_SAMPLE = {
            //         r: frameData[0],
            //         g: frameData[1],
            //         b: frameData[2]
            //     };
            //     frameData[3] = 0;

            //     // Start from px 2, since we sampled and removed above
            //     for (let i = 4; i < len; i += 4) {
            //         const r = (frameData[i] - DFV_BG_COLOR_SAMPLE.r);
            //         const g = (frameData[i+1] - DFV_BG_COLOR_SAMPLE.g);
            //         const b = (frameData[i+2] - DFV_BG_COLOR_SAMPLE.b);

            //         if((r >= 0 && g >= 0 && b >=0 && r <= COLOR_CHANNEL_ACCURACY && g <= COLOR_CHANNEL_ACCURACY && b <= COLOR_CHANNEL_ACCURACY)
            //             || (r <= 0 && g <= 0 && b <=0 && r >= COLOR_CHANNEL_ACCURACY_INVERSE && g >= COLOR_CHANNEL_ACCURACY_INVERSE && b >= COLOR_CHANNEL_ACCURACY_INVERSE)) {
            //             frameData[i+3] = 0;
            //         }
            //     }
            //     context.putImageData(frame, x, y);
            // }
            // frameData = null;
            // frame = null;
        }
    }
};

const DFVPlayerCanvas = ({ stream, setStreamError, setIsLoading, setAutoPlayError, autoPlayError }) => {
    const frameDrawer = useRef(null);
    const videoRef = useRef(null);
    const videoRes = useRef(VIDEO_RES_DEFAULTS);
    const dfvCanvasContext = useRef(null);

    const canvasMultiRef = useCallback((node) => {
        if (node !== null) {
            const context = dfvCanvasContext.current = node?.getContext('2d', CANVAS_CONTEXT_CONFIG);
            // If context creation was a success
            if (context) {
                context.imageSmoothingEnabled = true;
                context.imageSmoothingQuality = SMOOTH;
            }
        } else if (dfvCanvasContext.current !== null) {
            if (dfvCanvasContext.current.canvas) {
                dfvCanvasContext.current.canvas.width = 1;
                dfvCanvasContext.current.canvas.height = 1;
            }
            dfvCanvasContext.current = null;
        }
    }, []);

    useEffect(() => {
        let drawFrameCallback = () => {
            const video = videoRef.current;
            if (video && video.videoWidth) {
                renderFrame(dfvCanvasContext.current, video, videoRes);
            }
        };

        const cleanup = () => {
            if (frameDrawer.current) {
                frameDrawer.current.destroy();
                frameDrawer.current = null;
                drawFrameCallback = null;
            }
        };
        cleanup();
        frameDrawer.current = new FPSLocker(drawFrameCallback, 30 /* currently gcoder is set to 30fps */);

        return cleanup;
    }, []);

    return (
        <>
            <DFVPlayer
                ref={videoRef}
                hidden={true}
                stream={stream}
                setStreamError={setStreamError}
                setIsLoading={setIsLoading}
                frameDrawer={frameDrawer}
                EventBus={CanvasVideoEventBus}
                setAutoPlayError={setAutoPlayError}
                autoPlayError={autoPlayError}
            />
            <DynamicFieldViewCanvas
                ref={canvasMultiRef}
                width={videoRes.current.width}
                height={videoRes.current.height}
            />
        </>
    );
};

DFVPlayerCanvas.displayName = 'DFVPlayerCanvas';

DFVPlayerCanvas.propTypes = {
    stream: PropTypes.string,
    setStreamError: PropTypes.func,
    setIsLoading: PropTypes.func,
    setAutoPlayError: PropTypes.func,
    autoPlayError: PropTypes.bool
};

export default DFVPlayerCanvas;
